import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowsLeftRightIcon as ArrowsLeftRight, CheckCircleIcon as CheckCircle, ShieldCheckIcon as ShieldCheck, WarningIcon as Warning, LockIcon as Lock } from "@phosphor-icons/react";
import { formatApiError } from "@/lib/utils-app";
import { toast } from "sonner";
import PublicFooter from "@/components/PublicFooter";
import PromoCodeInput from "@/components/PromoCodeInput";
import CompBanner from "@/components/CompBanner";
import { isNativeApp, isIOS, webOrigin } from "@/lib/nativeBridge";
import IosPurchaseNotice from "@/components/IosPurchaseNotice";

/**
 * Per-transfer pay landing page reached from the email invite link.
 *
 * URL: `/transfers/pay/:id?email=<buyer_email>`
 *
 * States this page handles:
 *  - **Anonymous**: shows transfer summary + register/login form pre-filled
 *    with the invite email; on submit auto-redirects to Stripe checkout.
 *  - **Logged in as a different user**: shows a guarded warning explaining
 *    they must sign in with the email the seller sent the invite to.
 *  - **Logged in as the buyer**: shows summary + "Pay $15 & accept" button
 *    that immediately POSTs `/billing/checkout` with the transfer_id and
 *    redirects to Stripe.
 *  - **Already paid / already accepted / approved**: shows status pill +
 *    a link into the buyer's main `/transfers` page.
 *  - **Buyer already has an account but isn't logged in**: register attempt
 *    yields "email already registered" → page surfaces a "Sign in instead"
 *    fallback.
 */
export default function TransferPay() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const inviteEmail = (params.get("email") || "").toLowerCase();
  const { user, login, register } = useAuth();
  const navigate = useNavigate();

  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [mode, setMode] = useState("register"); // 'register' | 'login'
  const [formEmail, setFormEmail] = useState(inviteEmail);
  const [formName, setFormName] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [authBusy, setAuthBusy] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  // Promo code for the $15 administrative transfer fee. The /billing/
  // checkout endpoint already accepts {promo_code} and validates it
  // server-side; we just surface the input.
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    api
      .get(`/public/transfers/${id}`)
      .then((r) => setPreview(r.data))
      .catch((err) =>
        setPreviewError(
          err?.response?.status === 404
            ? "We couldn't find this transfer. The link may be invalid or the transfer may have been withdrawn."
            : formatApiError(err)
        )
      );
  }, [id]);

  // Once authenticated AND we know the user matches the invite, fire the
  // checkout automatically — no extra click required.
  const isCorrectBuyer =
    user && preview && (user.email || "").toLowerCase() === (preview.to_email || "").toLowerCase();
  const wrongBuyerLoggedIn =
    user && preview && !isCorrectBuyer;

  const startCheckout = async () => {
    if (payBusy) return;
    setPayBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: "transfer_ownership",
        origin_url: webOrigin(),
        transfer_id: id,
        promo_code: promo?.valid ? promo.code : undefined,
      });
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      // Free path (admin/promo) — go straight to /transfers.
      toast.success("Transfer accepted — now awaiting admin review.");
      navigate("/transfers", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
      setPayBusy(false);
    }
  };

  const submitAuth = async (e) => {
    e.preventDefault();
    if (authBusy) return;
    setAuthBusy(true);
    try {
      if (mode === "register") {
        await register(
          formEmail.trim().toLowerCase(),
          formPassword,
          formName.trim() || formEmail.split("@")[0],
        );
      } else {
        await login(formEmail.trim().toLowerCase(), formPassword);
      }
      // After auth, the AuthContext updates `user` and the next render
      // path auto-fires startCheckout via the button below.
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      // Common case: trying to register an email that already exists.
      if (mode === "register" && /already.*regist|exist/i.test(detail)) {
        toast.error("That email is already registered — sign in instead.");
        setMode("login");
      } else {
        toast.error(formatApiError(err));
      }
      setAuthBusy(false);
    }
  };

  // ----- Render --------------------------------------------------------

  if (previewError) {
    return (
      <PageShell>
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-10 text-center">
          <Warning size={36} weight="duotone" className="text-crimson mx-auto mb-3" />
          <h2 className="font-spectral text-2xl mb-2">Transfer not found</h2>
          <p className="text-[#6B6B6B]">{previewError}</p>
          <Link to="/" className="mt-6 inline-block text-sm font-semibold text-crimson underline">
            Return to homepage
          </Link>
        </div>
      </PageShell>
    );
  }

  if (!preview) {
    return (
      <PageShell>
        <div className="text-[#6B6B6B]">Loading transfer details…</div>
      </PageShell>
    );
  }

  // Already finalised states.
  const isAlreadyPaid = preview.paid || ["pending_admin_approve", "approved"].includes(preview.status);
  const isRejected = preview.status === "rejected";

  if (isAlreadyPaid) {
    return (
      <PageShell>
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-10 text-center">
          <CheckCircle size={36} weight="duotone" className="text-[#2F6E2F] mx-auto mb-3" />
          <h2 className="font-spectral text-2xl mb-2">Transfer already accepted</h2>
          <p className="text-[#6B6B6B]">
            This transfer has already been paid and is awaiting admin review (or has been
            approved). No further action needed.
          </p>
          {user && (
            <Link to="/transfers">
              <Button className="mt-5 rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white">
                View my transfers
              </Button>
            </Link>
          )}
        </div>
      </PageShell>
    );
  }
  if (isRejected) {
    return (
      <PageShell>
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-10 text-center">
          <Warning size={36} weight="duotone" className="text-crimson mx-auto mb-3" />
          <h2 className="font-spectral text-2xl mb-2">Transfer no longer active</h2>
          <p className="text-[#6B6B6B]">
            This transfer has been withdrawn or rejected. Contact the seller if you
            believe this is a mistake.
          </p>
        </div>
      </PageShell>
    );
  }

  const summary = (
    <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6 sm:p-8 mb-6" data-testid="transfer-pay-summary">
      <div className="flex items-center gap-2 mb-4">
        <ArrowsLeftRight size={22} weight="duotone" className="text-gold" />
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#6B6B6B]">Ownership transfer</p>
      </div>
      <h1 className="font-spectral text-3xl sm:text-4xl tracking-tight mb-1">
        Accept ownership of {preview.dog.name || "this dog"}
      </h1>
      <p className="text-[#6B6B6B] mb-5">
        From <strong className="text-ink">{preview.seller_name || "the current owner"}</strong>
        {preview.dog.breed ? ` · ${preview.dog.breed}` : ""}
        {preview.dog.registration_number ? ` · ${preview.dog.registration_number}` : ""}
      </p>
      {preview.message && (
        <blockquote className="border-l-2 border-[#C9A227] bg-[#FAFAFA] pl-4 py-2 italic text-sm text-[#555] mb-5">
          "{preview.message}"
        </blockquote>
      )}
      <div className="flex items-center justify-between gap-3 bg-[#FBF7EC] border border-[#E5D08A] rounded-xl p-4">
        <div className="text-sm text-[#8A6A00]">
          <strong>${preview.fee_usd.toFixed(2)}</strong> administrative fee, paid by the buyer.
          A registry admin will review and finalise the change after payment.
        </div>
        <Lock size={18} weight="duotone" className="text-gold flex-shrink-0" />
      </div>
    </div>
  );

  if (wrongBuyerLoggedIn) {
    return (
      <PageShell>
        {summary}
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-6">
          <Warning size={22} weight="duotone" className="text-crimson mb-3" />
          <h3 className="font-spectral text-xl mb-2">You're signed in as a different user</h3>
          <p className="text-sm text-[#6B6B6B] mb-4">
            This transfer is addressed to <strong>{preview.to_email}</strong>. Sign out and sign back
            in (or register) using that email to accept and pay.
          </p>
          <Button
            variant="outline"
            onClick={() => {
              localStorage.removeItem("token");
              window.location.reload();
            }}
            className="rounded-full"
            data-testid="transfer-pay-signout-btn"
          >
            Sign out
          </Button>
        </div>
      </PageShell>
    );
  }

  if (isCorrectBuyer) {
    return (
      <PageShell>
        {summary}
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 text-center">
          <ShieldCheck size={28} weight="duotone" className="text-[#2F6E2F] mx-auto mb-3" />
          <p className="text-ink font-medium mb-1">Signed in as {user.email}</p>
          <p className="text-xs text-[#6B6B6B] mb-5">Click below to pay securely via Stripe.</p>
          <div className="text-left mb-4">
            <CompBanner className="mb-3" />
            <PromoCodeInput
              kind="transfer_ownership"
              amount={preview.fee_usd}
              applied={promo}
              onApplied={(p) => {
                setPromo(p);
                toast.success(`${p.code} applied — final $${p.final.toFixed(2)}`);
              }}
              onCleared={() => setPromo(null)}
            />
          </div>
          {isIOS() && (
            <IosPurchaseNotice label="The transfer fee" showPlans={false} className="mb-3" />
          )}
          <Button
            onClick={startCheckout}
            disabled={payBusy}
            className={`rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white h-11 px-6 ${isIOS() ? "hidden" : ""}`}
            data-testid="transfer-pay-checkout-btn"
          >
            <CheckCircle size={16} className="mr-2" weight="duotone" />
            {payBusy
              ? "Redirecting…"
              : promo?.valid
              ? (isNativeApp() ? `Accept — $${promo.final.toFixed(2)} checkout in browser` : `Pay $${promo.final.toFixed(2)} & accept`)
              : (isNativeApp() ? `Accept — $${preview.fee_usd.toFixed(2)} checkout in browser` : `Pay $${preview.fee_usd.toFixed(2)} & accept`)}
          </Button>
        </div>
      </PageShell>
    );
  }

  // Anonymous: register or login form pre-filled with the invite email.
  return (
    <PageShell>
      {summary}
      <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-1">
          <h2 className="font-spectral text-2xl tracking-tight">
            {mode === "register" ? "Create your account" : "Sign in"}
          </h2>
        </div>
        <p className="text-sm text-[#6B6B6B] mb-5">
          {mode === "register"
            ? "We've pre-filled the email the seller sent the invite to. After registering, you'll be redirected to pay."
            : "Sign in with the email this transfer was sent to."}
        </p>
        <form onSubmit={submitAuth} className="space-y-4" data-testid="transfer-pay-auth-form">
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Email</Label>
            <Input
              type="email"
              value={formEmail}
              onChange={(e) => setFormEmail(e.target.value)}
              required
              className="mt-1 rounded-lg"
              data-testid="transfer-pay-email-input"
            />
            {inviteEmail && formEmail.toLowerCase() !== inviteEmail && (
              <p className="text-xs text-crimson mt-1">
                This invite was sent to <strong>{inviteEmail}</strong>. Use that exact email to claim the transfer.
              </p>
            )}
          </div>
          {mode === "register" && (
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                Your name
              </Label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Full name"
                className="mt-1 rounded-lg"
                data-testid="transfer-pay-name-input"
              />
            </div>
          )}
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Password
            </Label>
            <Input
              type="password"
              value={formPassword}
              onChange={(e) => setFormPassword(e.target.value)}
              required
              minLength={6}
              className="mt-1 rounded-lg"
              data-testid="transfer-pay-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={authBusy}
            className="w-full rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white h-11"
            data-testid="transfer-pay-auth-submit"
          >
            {authBusy
              ? "Working…"
              : mode === "register"
              ? "Register & continue to payment"
              : "Sign in & continue to payment"}
          </Button>
        </form>
        <button
          type="button"
          onClick={() => setMode(mode === "register" ? "login" : "register")}
          className="mt-4 text-sm text-[#6B6B6B] hover:text-ink underline"
          data-testid="transfer-pay-mode-toggle"
        >
          {mode === "register" ? "Already have an account? Sign in" : "New user? Create account"}
        </button>
      </div>
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <header className="border-b border-[#E5E5E5]/60 bg-white/80 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <img src="/logo-256.png" alt="" className="h-9 w-9 rounded-full object-contain" />
          <p className="font-spectral text-sm leading-tight text-ink">
            Armstrong's Supreme<br />Team K9 Registry
          </p>
        </div>
      </header>
      <main className="flex-1 max-w-3xl mx-auto w-full p-6 sm:p-10">{children}</main>
      <PublicFooter />
    </div>
  );
}
